import { gsap } from 'gsap';

// ============================================================================
// WHEEL ANIMATION CONSTANTS
// ============================================================================

// Default constants used by WheelPicker
export const WHEEL_DEFAULTS = {
  frontTickMs: 130,
  tickMs: 180,
  endTickMs: 360,
  backShiftStart: 0.45,
  backShiftEnd: 0.55,
  spinMinUnits: 28,
  spinMaxUnits: 40,
};

// Constants used by character/stage selection (faster animation)
export const WHEEL_SELECTION_TIMINGS = {
  frontTickMs: 80,
  tickMs: 150,
  endTickMs: 420,
  backShiftStart: 0.38,
  backShiftEnd: 0.68,
  spinMinUnits: 28,
  spinMaxUnits: 40,
};

// Vertical movement values for text animation
export const WHEEL_ENTRY_Y = -28;
export const WHEEL_EXIT_Y = 28;

// ============================================================================
// ANIMATE WHEEL TEXT
// ============================================================================
/**
 * Animates the text in the wheel display with a fade-in-top to fade-out-bottom
 * effect, creating the illusion of text rolling through the wheel.
 *
 * @param {HTMLElement} target - The DOM element to animate
 * @param {string} text - The text to display
 * @param {number} stepMs - Duration of this step in milliseconds
 */
export const animateWheelText = (target, text, stepMs) => {
  if (!target) {
    return;
  }

  const stepDuration = Math.max(0.08, stepMs / 1000);
  gsap.killTweensOf(target);
  target.textContent = text;
  const stepInDuration = stepDuration * 0.5;
  const stepOutDuration = stepDuration * 0.5;
  const tl = gsap.timeline({ defaults: { ease: 'none', overwrite: 'auto' } });

  // Fade in from top
  tl.fromTo(
    target,
    { y: WHEEL_ENTRY_Y, autoAlpha: 0 },
    { y: 0, autoAlpha: 1, duration: stepInDuration },
  );
  // Fade out to bottom
  tl.to(target, { y: WHEEL_EXIT_Y, autoAlpha: 0, duration: stepOutDuration });
};

// ============================================================================
// SPIN WHEEL
// ============================================================================
/**
 * Core wheel spinning animation logic. Handles the entire spin sequence:
 * - Fast spins at the start (frontTickMs)
 * - Gradual slowdown during transition (backShiftStart to backShiftEnd)
 * - Very slow spins near the end (endTickMs)
 *
 * @param {HTMLElement} targetElement - DOM element where wheel text is displayed
 * @param {Array<string>} items - Array of items to cycle through
 * @param {Object} config - Configuration object with animation timings:
 *   - frontTickMs: Initial fast tick duration (ms)
 *   - tickMs: Middle tick duration (ms)
 *   - endTickMs: Final slow tick duration (ms)
 *   - backShiftStart: Progress % when slowdown begins (0-1)
 *   - backShiftEnd: Progress % when slowdown ends (0-1)
 *   - spinMinUnits: Minimum spin count (affects total duration)
 *   - spinMaxUnits: Maximum spin count (affects total duration)
 * @param {Function} onSpinStep - Callback fired for each step during spin
 *   Called with: (currentItem)
 * @param {Function} onSelect - Callback fired when spin completes with final selection
 *   Called with: (finalItem)
 */
export const spinWheel = (
  targetElement,
  items,
  config,
  onSpinStep,
  onSelect,
) => {
  if (!targetElement || !items.length) {
    return;
  }

  // Calculate random spin duration
  // Multiply by 250ms per unit to get total spin time
  const randomUnits = Math.floor(
    Math.random() * (config.spinMaxUnits - config.spinMinUnits + 1) +
      config.spinMinUnits,
  );
  const spinDurationMs = 250 * randomUnits;
  const spinStartMs = Date.now();

  let i = 0;
  let lastShownItem = items[0] || '?';
  let isFinalized = false;

  /**
   * Finalize the spin and display the final selected item
   */
  const finalizeSpin = () => {
    if (isFinalized) {
      return;
    }

    isFinalized = true;
    const finalItem = targetElement.textContent || lastShownItem;
    gsap.killTweensOf(targetElement);
    targetElement.textContent = finalItem;

    // Fade in final selection with ease
    gsap.to(targetElement, {
      y: 0,
      autoAlpha: 1,
      duration: 0.12,
      ease: 'power1.out',
    });

    if (typeof onSelect === 'function') {
      onSelect(finalItem);
    }
  };

  /**
   * Run one step of the spin animation
   * Calculates which tick duration to use based on progress through the spin
   */
  const runSpinStep = () => {
    if (isFinalized) {
      return;
    }

    const elapsedMs = Date.now() - spinStartMs;

    // Check if spin is complete
    if (elapsedMs >= spinDurationMs) {
      finalizeSpin();
      return;
    }

    // Calculate progress (0 to 1)
    const progress = elapsedMs / spinDurationMs;
    let nextTickMs = config.frontTickMs; // Default to fast speed

    // ========================================================================
    // SPEED TRANSITIONS
    // ========================================================================

    // During transition zone: gradually slow down from front to middle speed
    if (progress >= config.backShiftStart && progress < config.backShiftEnd) {
      const handoffProgress =
        (progress - config.backShiftStart) /
        (config.backShiftEnd - config.backShiftStart);
      nextTickMs =
        config.frontTickMs +
        (config.tickMs - config.frontTickMs) * handoffProgress;
    }
    // After transition: gradually slow down from middle to final speed
    else if (progress >= config.backShiftEnd) {
      const slowdownProgress =
        (progress - config.backShiftEnd) / (1 - config.backShiftEnd);
      const easedSlowdown = slowdownProgress * slowdownProgress;
      nextTickMs =
        config.tickMs + (config.endTickMs - config.tickMs) * easedSlowdown;
    }

    // Get next item and animate it
    const nextItem = items[i++ % items.length];
    lastShownItem = nextItem;

    if (typeof onSpinStep === 'function') {
      onSpinStep(nextItem);
    }

    animateWheelText(targetElement, nextItem, nextTickMs);
    setTimeout(runSpinStep, nextTickMs);
  };

  // Start the animation loop
  runSpinStep();
  // Set a timeout to finalize (backup in case timing is off)
  setTimeout(finalizeSpin, spinDurationMs);
};
